import React, { useMemo, useState } from "react";
import { Database, PanelLeftClose, PanelLeftOpen, Search } from "lucide-react";
import { Button } from "../ui/button";

const getTableInitials = (tableName) =>
  tableName
    .split("_")
    .filter(Boolean)
    .map((part) => part[0]?.toUpperCase() || "")
    .join("")
    .slice(0, 4);

function TablesSidebar({
  tables,
  selectedTableName,
  isCollapsed,
  onToggleCollapse,
  onSelectTable,
}) {
  const [searchTerm, setSearchTerm] = useState("");

  const filteredTables = useMemo(() => {
    return tables.filter((table) =>
      table.table.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, tables]);

  return (
    <aside
      className={`flex h-full shrink-0 flex-col overflow-hidden border-y border-r border-gray-200 bg-white/95 shadow-sm transition-all dark:border-gray-800 dark:bg-gray-900/95 ${isCollapsed ? "w-20" : "w-80"}`}
    >
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-4 dark:border-gray-800">
        {!isCollapsed && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
              Tables
            </p>
            <h2 className="mt-1 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Explore Tables
            </h2>
          </div>
        )}
        <Button variant="ghost" size="small" onClick={onToggleCollapse} className="h-9 w-9 p-0">
          {isCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
        </Button>
      </div>

      {!isCollapsed && (
        <div className="border-b border-gray-200 px-4 py-4 dark:border-gray-800">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search tables..."
              className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-blue-500 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100"
            />
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3">
        <div className="space-y-2">
          {filteredTables.map((table) => {
            const isActive = table.table === selectedTableName;
            const columnsCount = Object.keys(table.schema?.properties || {}).length;
            return (
              <button
                key={table.table}
                onClick={() => onSelectTable(table.table)}
                title={isCollapsed ? table.table : undefined}
                className={`w-full rounded-xl border px-3 py-3 text-left transition ${isActive ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-500 dark:bg-blue-950/50 dark:text-blue-200" : "border-transparent bg-gray-50 text-gray-700 hover:border-gray-200 hover:bg-white dark:bg-gray-950 dark:text-gray-300 dark:hover:border-gray-800 dark:hover:bg-gray-900"}`}
              >
                <div className={`flex items-center ${isCollapsed ? "justify-center" : "gap-3"}`}>
                  <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${isActive ? "bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200" : "bg-gray-200 text-gray-600 dark:bg-gray-800 dark:text-gray-300"}`}>
                    {isCollapsed ? (
                      <span className="text-xs font-semibold tracking-wide">{getTableInitials(table.table)}</span>
                    ) : (
                      <Database className="h-4 w-4" />
                    )}
                  </div>
                  {!isCollapsed && (
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-semibold">{table.table}</div>
                      <div className="mt-1 flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span>{Number(table.row_count || 0).toLocaleString()} rows</span>
                        <span>•</span>
                        <span>{columnsCount} cols</span>
                      </div>
                    </div>
                  )}
                </div>
              </button>
            );
          })}
          {filteredTables.length === 0 && !isCollapsed && (
            <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
              No table matches this search.
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}

export default TablesSidebar;
